import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { ArrowUpCircle, ArrowDownCircle, CreditCard, TrendingUp, Lock } from 'lucide-react';
import { Skeleton } from '../../components/ui/skeleton';

const CustomerDashboard = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await api.get('/me/balance');
      setBalance(response.data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-2">Bem-vindo à sua conta digital</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Bem-vindo à sua conta digital</p>
      </div>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
            Conta Digital
          </CardTitle>
          <CardDescription>Dados da sua conta de pagamento</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground">Agencia</p>
              <p className="font-mono text-lg font-semibold">{balance?.branch_number || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Conta</p>
              <p className="font-mono text-lg font-semibold">
                {balance?.account_number ? `${balance.account_number}-${balance?.account_digit || '0'}` : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tipo</p>
              <p className="font-medium">{balance?.account_label || 'Conta Digital'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/20">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Disponível</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="balance-available">
              {formatCurrency(balance?.available || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valores Bloqueados</CardTitle>
            <Lock className="h-4 w-4 text-warning" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="balance-holds">
              {formatCurrency(balance?.holds || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Limite Cheque Especial</CardTitle>
            <CreditCard className="h-4 w-4 text-accent" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="overdraft-limit">
              {formatCurrency(balance?.overdraft_limit || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-border/50">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Cheque Especial Usado</CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-destructive" strokeWidth={1.5} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight" data-testid="overdraft-used">
              {formatCurrency(balance?.overdraft_used || 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle>Resumo da Conta</CardTitle>
          <CardDescription>Visão geral das suas finanças</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-border/50">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success/10">
                  <ArrowUpCircle className="h-5 w-5 text-success" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium">Saldo Total</p>
                  <p className="text-sm text-muted-foreground">Valor total na conta</p>
                </div>
              </div>
              <span className="text-lg font-bold font-mono">{formatCurrency(balance?.total || 0)}</span>
            </div>

            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10">
                  <CreditCard className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium">Crédito Disponível</p>
                  <p className="text-sm text-muted-foreground">Limite restante do cheque especial</p>
                </div>
              </div>
              <span className="text-lg font-bold font-mono">
                {formatCurrency((balance?.overdraft_limit || 0) - (balance?.overdraft_used || 0))}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerDashboard;
